<script>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import FunnelAPI from '/plugins/kanban-board/src/api/funnel';

export default {
  props: {
    modelValue: {
      type: [Object, Array],
      default: () => ({ funnel_id: null, funnel_stage: null }),
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const funnels = ref([]);
    const selectedFunnel = ref(null);
    const selectedStage = ref(null);
    const loading = ref(false);

    const availableStages = computed(() => {
      if (!selectedFunnel.value) {
        return [];
      }
      
      if (!selectedFunnel.value.stages) {
        return [];
      }
      
      const stages = Object.entries(selectedFunnel.value.stages)
        .map(([id, stage]) => ({
          id,
          name: stage.name || id,
          position: stage.position || 0,
        }))
        .sort((a, b) => a.position - b.position);
      
      return stages;
    });

    const fetchFunnels = async () => {
      loading.value = true;
      try {
        const response = await FunnelAPI.get();
        funnels.value = (response.data || []).map(funnel => ({
          ...funnel,
          id: String(funnel.id),
        }));
      } catch (error) {
        console.error('Erro ao buscar funis:', error);
      } finally {
        loading.value = false;
      }
    };

    const updateValue = () => {
      const value = {
        funnel_id: selectedFunnel.value ? parseInt(selectedFunnel.value.id, 10) : null,
        funnel_stage: selectedStage.value?.id || null,
      };
      emit('update:modelValue', value);
    };

    watch(selectedFunnel, (newFunnel, oldFunnel) => {
      if (newFunnel && newFunnel !== oldFunnel) {
        selectedStage.value = null;
        updateValue();
      }
    });

    watch(selectedStage, () => {
      updateValue();
    });

    const syncFromModelValue = async () => {
      // O modelValue pode vir como objeto direto { funnel_id, funnel_stage } 
      // ou como array [{ funnel_id, funnel_stage }]
      const modelData = Array.isArray(props.modelValue) 
        ? props.modelValue[0] 
        : props.modelValue;
      
      if (!modelData || !modelData.funnel_id) return;
      
      // Se não temos funis ainda, não podemos sincronizar
      if (funnels.value.length === 0) return;
      
      const funnel = funnels.value.find(f => String(f.id) === String(modelData.funnel_id));
      if (!funnel) return;
      
      // Atualiza o funil se necessário
      if (selectedFunnel.value?.id !== funnel.id) {
        selectedFunnel.value = funnel;
        // Aguarda o próximo tick para o computed availableStages ser atualizado
        await nextTick();
      }
      
      // Agora tenta encontrar o stage
      if (modelData.funnel_stage) {
        const stageId = modelData.funnel_stage;
        // Aguarda para garantir que availableStages está atualizado
        await nextTick();
        
        // Tenta encontrar o stage nos availableStages
        let stage = availableStages.value.find(s => s.id === stageId);
        
        if (stage) {
          selectedStage.value = stage;
        } else {
          // Se não encontrou, pode ser que os stages ainda não foram processados
          // Tenta buscar diretamente no funnel.stages
          if (funnel.stages && funnel.stages[stageId]) {
            stage = {
              id: stageId,
              name: funnel.stages[stageId].name || stageId,
              position: funnel.stages[stageId].position || 0,
            };
            selectedStage.value = stage;
          } else {
            // Última tentativa após um pequeno delay
            setTimeout(() => {
              const stageRetry = availableStages.value.find(s => s.id === stageId);
              if (stageRetry) {
                selectedStage.value = stageRetry;
              }
            }, 200);
          }
        }
      }
    };

    watch(() => props.modelValue, syncFromModelValue, { deep: true, immediate: true });

    watch(funnels, () => {
      if (funnels.value.length > 0) {
        syncFromModelValue();
      }
    });

    // Watch adicional para quando os stages ficam disponíveis e precisamos sincronizar
    watch(availableStages, (newStages) => {
      if (newStages.length === 0) return;
      
      const modelData = Array.isArray(props.modelValue) 
        ? props.modelValue[0] 
        : props.modelValue;
      
      // Se temos um funnel_stage no modelValue mas não temos selectedStage, tenta sincronizar
      if (modelData?.funnel_stage && !selectedStage.value) {
        const stageId = modelData.funnel_stage;
        const stage = newStages.find(s => s.id === stageId);
        if (stage) {
          selectedStage.value = stage;
        }
      }
    });

    onMounted(async () => {
      await fetchFunnels();
      syncFromModelValue();
    });

    return {
      funnels,
      selectedFunnel,
      selectedStage,
      availableStages,
      loading,
    };
  },
};
</script>

<template>
  <div class="multiselect-wrap--small flex flex-col gap-2 mt-1">
    <multiselect
      v-model="selectedFunnel"
      :options="funnels"
      track-by="id"
      label="name"
      :placeholder="$t('AUTOMATION.ACTION.FUNNEL_SELECT_PLACEHOLDER')"
      :allow-empty="false"
      :max-height="160"
      :loading="loading"
    >
      <template #noOptions>
        {{ $t('FORMS.MULTISELECT.NO_OPTIONS') }}
      </template>
    </multiselect>
    <multiselect
      :key="selectedFunnel?.id || 'no-funnel'"
      v-model="selectedStage"
      :options="availableStages"
      track-by="id"
      label="name"
      :placeholder="$t('AUTOMATION.ACTION.STAGE_SELECT_PLACEHOLDER')"
      :allow-empty="false"
      :max-height="160"
      :disabled="!selectedFunnel || availableStages.length === 0"
    >
      <template #noOptions>
        {{ $t('FORMS.MULTISELECT.NO_OPTIONS') }}
      </template>
    </multiselect>
  </div>
</template>

<style scoped>
.multiselect {
  margin: 0.25rem 0;
}
</style>
